import { useMemo } from "react";
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { ExternalLink, ListChecks, Video } from "lucide-react";
import ReactMarkdown from "react-markdown";

import type { AssociatedObject } from "./types";
import { getMeetingViewModel, safeDate, type MeetingRecordLike } from "./meetingUtils";

function formatTimeRange(start: Date | null, end: Date | null): string {
  if (!start) return "—";
  const sameDay =
    !!end &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const startDate = start.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  const startTime = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (!end) return `${startDate} • ${startTime}`;

  const endDate = end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  const endTime = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (sameDay) return `${startDate} • ${startTime} – ${endTime}`;
  return `${startDate} • ${startTime} – ${endDate} • ${endTime}`;
}

function formatDurationSeconds(seconds?: number): string {
  if (!seconds || seconds <= 0) return "—";
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours}h`);
  if (remainingMinutes) parts.push(`${remainingMinutes}m`);
  return parts.join(" ");
}

type MeetingRecord = Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text"> | MeetingRecordLike;

const MeetingDetailsPanel = ({ meeting }: { meeting: MeetingRecord | null }) => {
  const view = useMemo(() => (meeting ? getMeetingViewModel(meeting) : null), [meeting]);

  const startDate = useMemo(() => safeDate(view?.startTime), [view?.startTime]);
  const endDate = useMemo(() => safeDate(view?.endTime), [view?.endTime]);
  const isUpcoming = useMemo(() => (startDate ? startDate > new Date() : false), [startDate]);

  const headerAvatar = useMemo(() => (view?.title || "?").slice(0, 1).toUpperCase(), [view?.title]);
  const timeLabel = useMemo(() => formatTimeRange(startDate, endDate), [startDate, endDate]);
  const durationLabel = useMemo(() => {
    if (view?.durationSeconds) return formatDurationSeconds(view.durationSeconds);
    if (!startDate || !endDate) return "—";
    const seconds = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 1000));
    return formatDurationSeconds(seconds);
  }, [view?.durationSeconds, startDate, endDate]);

  const participantAvatars = useMemo(() => (view ? view.participants.slice(0, 6) : []), [view]);

  const remainingParticipantsLabel = useMemo(() => {
    if (!view) return "";
    const remaining = view.participants.slice(6);
    if (remaining.length === 0) return "";
    const preview = remaining.slice(0, 10).join(", ");
    if (remaining.length <= 10) return preview;
    return `${preview} +${remaining.length - 10} more`;
  }, [view]);

  if (!view) {
    return (
      <Text size="sm" c="text.5">
        Select a meeting to view details.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Avatar radius="xl" size={40} variant="light" color="main">
            {headerAvatar}
          </Avatar>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} c="text.9" lineClamp={2}>
              {view.title}
            </Text>
            <Text size="sm" c="text.6" lineClamp={2}>
              {timeLabel}
            </Text>
          </Stack>
        </Group>

        <Stack gap={6} align="flex-end">
          {isUpcoming ? (
            <Badge variant="light" color="main" radius="md">
              Upcoming
            </Badge>
          ) : (
            <Badge variant="light" color="gray" radius="md">
              Past
            </Badge>
          )}
          {view.meetingUrl && (
            <Button component="a" href={view.meetingUrl} target="_blank" rel="noreferrer" variant="light" color="main" size="xs">
              Join
            </Button>
          )}
        </Stack>
      </Group>

      <Stack gap={8}>
        <Text size="xs" fw={700} c="text.5" tt="uppercase" className="tracking-wider">
          Details
        </Text>
        <Paper withBorder radius="md" p={0}>
          <Stack gap={0}>
            <Group justify="space-between" wrap="nowrap" className="px-3 py-2 hover:bg-gray-50 transition-colors">
              <Text size="xs" c="text.5" fw={600}>
                Organizer
              </Text>
              <Text size="xs" c={view.organizer ? "text.8" : "text.4"} ta="right" lineClamp={1} style={{ maxWidth: "70%" }}>
                {view.organizer || "—"}
              </Text>
            </Group>
            <Divider />
            <Group justify="space-between" wrap="nowrap" className="px-3 py-2 hover:bg-gray-50 transition-colors">
              <Text size="xs" c="text.5" fw={600}>
                Duration
              </Text>
              <Text size="xs" c={durationLabel === "—" ? "text.4" : "text.8"} ta="right">
                {durationLabel}
              </Text>
            </Group>
            <Divider />
            <Group justify="space-between" wrap="nowrap" className="px-3 py-2 hover:bg-gray-50 transition-colors">
              <Text size="xs" c="text.5" fw={600}>
                Participants
              </Text>
              {view.participants.length === 0 ? (
                <Text size="xs" c="text.4" ta="right">
                  —
                </Text>
              ) : (
                <Group gap={-6} wrap="nowrap" justify="flex-end">
                  {participantAvatars.map((participant) => (
                    <Tooltip key={participant} label={participant} withArrow>
                      <Avatar className="z-10" radius="xl" name={participant} ml={-6} size={20} variant="filled" color="initials">
                        {participant.slice(0, 1).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ))}
                  {view.participants.length > participantAvatars.length && (
                    <Tooltip
                      label={remainingParticipantsLabel || `${view.participants.length - participantAvatars.length} more`}
                      withArrow
                      multiline
                      w={260}
                    >
                      <Avatar radius="xl" size={20} variant="outline" color="main" ml={-6}>
                        +{view.participants.length - participantAvatars.length}
                      </Avatar>
                    </Tooltip>
                  )}
                </Group>
              )}
            </Group>
            {(view.provider || view.language || view.recordingUrl) && (
              <>
                <Divider />
                <Group justify="space-between" wrap="nowrap" className="px-3 py-2 hover:bg-gray-50 transition-colors">
                  <Text size="xs" c="text.5" fw={600}>
                    Recording
                  </Text>
                  <Group gap={6}>
                    {view.provider && (
                      <Badge variant="outline" size="xs" radius="md" color="gray">
                        {view.provider}
                      </Badge>
                    )}
                    {view.language && (
                      <Badge variant="light" size="xs" radius="md" color="gray">
                        {view.language}
                      </Badge>
                    )}
                    {view.recordingUrl && (
                      <Button
                        component="a"
                        href={view.recordingUrl}
                        target="_blank"
                        rel="noreferrer"
                        variant="subtle"
                        color="gray"
                        size="compact-xs"
                        rightSection={<ExternalLink size={12} />}
                      >
                        Watch
                      </Button>
                    )}
                  </Group>
                </Group>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Recording Summary */}
      {view.summaryMd && (
        <Stack gap={8}>
          <Text size="xs" fw={700} c="text.5" tt="uppercase" className="tracking-wider">
            Summary
          </Text>
          <Paper withBorder radius="md" p="md">
            <div className="prose prose-sm max-w-none text-gray-700 [&_p]:text-sm [&_p]:leading-relaxed [&_li]:text-sm">
              <ReactMarkdown>{view.summaryMd}</ReactMarkdown>
            </div>
          </Paper>
        </Stack>
      )}

      {/* Action Items */}
      {view.actionItems.length > 0 && (
        <Stack gap={8}>
          <Group gap={6}>
            <ListChecks size={14} className="text-emerald-600" />
            <Text size="xs" fw={700} c="text.5" tt="uppercase" className="tracking-wider">
              Action Items ({view.actionItems.length})
            </Text>
          </Group>
          <Paper withBorder radius="md" p={0}>
            <Stack gap={0}>
              {view.actionItems.map((item, i) => (
                <Group key={i} wrap="nowrap" className="px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                  <Text size="xs" c="text.5" fw={600} className="shrink-0">
                    {i + 1}.
                  </Text>
                  <Text size="xs" c="text.8">
                    {item}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* Transcript */}
      {view.transcriptText && (
        <Stack gap={8}>
          <Text size="xs" fw={700} c="text.5" tt="uppercase" className="tracking-wider">
            Transcript
          </Text>
          <Paper withBorder radius="md" p="md" bg="gray.0">
            <Text size="xs" c="text.6" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
              {view.transcriptText}
            </Text>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
};

export default MeetingDetailsPanel;
